import { TRIAL_BOOKING_STATE } from '@common/enums'
import { staticFile } from '@common/utils'
import AlertMessage from '@components/atoms/alert-message'
import MsgTrans from '@components/common/i18n/message-trans'
import Spinner from '@components/common/spinner'
import TimezoneModal from '@components/trial-class/timezone-modal'
import useResolution from '@hooks/useResolution'
import useSegment, { SegmentEventEnum } from '@hooks/useSegment'
import { BOOKING_PAGE_HEADING } from '@lib/enums/booking'
import date, { formatTimeByTimezone } from '@lib/utils/date.utils'
import { trackTrialSlotViewed } from '@lib/utils/trial-class'
import { t, Trans } from '@lingui/macro'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { i18n } from '@lingui/core'
import Modal from '@components/common/modal'
import Button from '@components/atoms/button'

type Props = {
  slots: any
  selectedDate: string
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>
  selectedTime: string
  setSelectedTime: React.Dispatch<React.SetStateAction<string>>
  onBook: () => void
  isBooking: boolean
  selectedTimezone: string
  setSelectedTimezone?: React.Dispatch<React.SetStateAction<string>>
  isInstantClassAvailable?: boolean
  setSelectSlotAllowed?: React.Dispatch<React.SetStateAction<boolean>>
  findInstantClass?: string
  title?: string
  timezoneAlertBgColor?: 'blue' | 'yellow'
  showLimitedSlotsAlert?: boolean
  showBookingStats?: boolean
  ctaText?: string
  setNumDaysToShow?: React.Dispatch<React.SetStateAction<number>>
  setIsShowingMoreSlots?: React.Dispatch<React.SetStateAction<boolean>>
  isShowingMoreSlots?: boolean
  isShowMoreSlotsOption?: boolean
  onGoBack?: () => void
  error?: string
  segmentTrackingEvents?: {
    clickDate: SegmentEventEnum
    clickTime: SegmentEventEnum
  }

  isInteachertrialClassroom?: boolean
  studentName?: string
  studentEmail?: string
  studentGrade?: string
  isCompetitionRegistration?: boolean
  isCodingProject?: boolean
  isMathClass?: boolean
}

const LIMITED_SLOTS_THRESHOLD = 10
const MOBILE_SLOT_ROW_COUNT = 5.25
const DESKTOP_SLOT_ROW_COUNT = 3.25
const MOBILE_SLOT_ROW_HEIGHT = 47
const DESKTOP_SLOT_ROW_HEIGHT = 50

const AvailableSlots = ({
  slots,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  onBook,
  isBooking,
  findInstantClass,
  selectedTimezone,
  setSelectedTimezone,
  title,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  timezoneAlertBgColor = 'blue',
  showLimitedSlotsAlert = false,
  showBookingStats = false,
  ctaText = t({ id: 'trialClass.booking.ctaText', message: `Book now` }),
  onGoBack,
  error = '',
  segmentTrackingEvents,
  isInteachertrialClassroom,
  studentName,
  studentEmail,
  studentGrade,
  isCompetitionRegistration,
  isCodingProject,
  isMathClass,
  setNumDaysToShow,
  setIsShowingMoreSlots,
  isShowingMoreSlots,
  isShowMoreSlotsOption,
}: Props) => {
  const track = useSegment()
  const [slotsToDisplay, setSlotsToDisplay] = useState([])
  const [isTimezoneModalOpen, setIsTimezoneModalOpen] = useState(false)
  const router = useRouter()
  const { course } = router.query
  const [showModal, setShowModal] = useState(false)
  const sanitizedCourse =
    typeof course === 'string' ? course.replace(/-/g, '') : ''

  const handleBooking = () => {
    onBook()
    setShowModal(false)
  }
  const handleChangeSlot = () => {
    setShowModal(false)
  }

  const {
    sizes: { sm },
  } = useResolution()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        trackTrialSlotViewed(course)
      } catch (error) {
        console.error('Error tracking trial slot view:', error)
      }
    }
  }, [])

  useEffect(() => {
    setSlotsToDisplay(slots[selectedDate])
  }, [selectedDate])

  return (
    <div data-testid="available-slots" className="flex flex-col">
      {title ? (
        <h4 className="text-xl leading-tight text-grey font-700">{title}</h4>
      ) : (
        <>
          {findInstantClass === TRIAL_BOOKING_STATE.FAIL ? (
            // TODO: Remove condition
            <div className="text-3xl text-grey font-600">
              <div>Sorry! No live teachers available now.</div>
              <div>Please book a convenient slot</div>
            </div>
          ) : (
            <>
              {!isCompetitionRegistration && !isCodingProject && (
                <div className="mb-4">
                  <h4 className="text-3xl leading-tight text-grey font-600 mb-3">
                    <MsgTrans
                      msg={
                        course
                          ? BOOKING_PAGE_HEADING[`${sanitizedCourse}`]
                          : BOOKING_PAGE_HEADING.coding
                      }
                    />
                  </h4>
                  {!isInteachertrialClassroom && studentGrade && (
                    <div className="text-base text-grey-800 flex flex-wrap items-center font-medium">
                      <div
                        title="Ensure your grade is correct so that right teacher can be assigned"
                        className="flex items-center"
                      >
                        <span>
                          You&apos;re in{' '}
                          <span className="font-600">{studentGrade}</span>
                        </span>
                        <button
                          className="ml-1 text-blue-200 text-sm underline cursor-pointer hover:text-orange focus:outline-none"
                          onClick={() => {
                            onGoBack && onGoBack()
                          }}
                          title="Change your grade to get matched with the right teacher"
                        >
                          <Trans>Change</Trans>
                        </button>
                      </div>
                      <span className="mx-2">•</span>
                      <span>
                        Local time:{' '}
                        <span className="font-600">
                          {date().tz(selectedTimezone).format('hh:mm A')}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
              {isCodingProject && (
                <div className="mb-4">
                  <h4 className="text-xl md:text-3xl leading-tight text-grey font-600 mb-3">
                    <Trans>
                      Schedule a free mentorship session with a Computer Science
                      expert
                    </Trans>
                  </h4>
                  {!isInteachertrialClassroom && studentGrade && (
                    <div className="text-base text-grey-800 flex flex-wrap items-center font-medium">
                      <div
                        title="Ensure your grade is correct so that right teacher can be assigned"
                        className="flex items-center"
                      >
                        <span>
                          You&apos;re in{' '}
                          <span className="font-600">{studentGrade}</span>
                        </span>
                        <button
                          className="ml-1 text-blue-200 text-sm underline cursor-pointer hover:text-orange focus:outline-none"
                          onClick={() => {
                            onGoBack && onGoBack()
                          }}
                          title="Change your grade to get matched with the right teacher"
                        >
                          <Trans>Change</Trans>
                        </button>
                      </div>
                      <span className="mx-2">•</span>
                      <span>
                        Local time:{' '}
                        <span className="font-600">
                          {date().tz(selectedTimezone).format('hh:mm A')}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
              {isCompetitionRegistration && (
                <div className="mb-4">
                  <h4 className="text-xl md:text-3xl leading-tight text-grey font-600 mb-3">
                    <Trans>
                      Schedule a free mentorship session with a Computer Science
                      expert & kickstart your competition preparation
                    </Trans>
                  </h4>
                  {!isInteachertrialClassroom && studentGrade && (
                    <div className="text-base text-grey-800 flex flex-wrap items-center font-medium">
                      <div
                        title="Ensure your grade is correct so that right teacher can be assigned"
                        className="flex items-center"
                      >
                        <span>
                          You&apos;re in{' '}
                          <span className="font-600">{studentGrade}</span>
                        </span>
                        <button
                          className="ml-1 text-blue-200 text-sm underline cursor-pointer hover:text-orange focus:outline-none"
                          onClick={() => {
                            onGoBack && onGoBack()
                          }}
                          title="Change your grade to get matched with the right teacher"
                        >
                          <Trans>Change</Trans>
                        </button>
                      </div>
                      <span className="mx-2">•</span>
                      <span>
                        Local time:{' '}
                        <span className="font-600">
                          {date().tz(selectedTimezone).format('hh:mm A')}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {isInteachertrialClassroom && (
        <div className="flex flex-col mt-2 bg-grey-200 rounded p-5 py-3">
          <p>
            Name: <span className="font-600 text-orange">{studentName}</span>
          </p>
          <p>
            Email: <span className="font-600 text-orange">{studentEmail}</span>
          </p>
          <p>
            Grade: <span className="font-600 text-orange">{studentGrade}</span>
          </p>
        </div>
      )}

      <div className="flex flex-col mt-3">
        <span className="font-600 text-grey text-xl">
          <Trans id="trialClass.booking.selectDate">Select a date</Trans>
        </span>
        <div className="grid grid-cols-2 mt-2 lg:grid-cols-3 gap-x-3 gap-y-3">
          {Object.keys?.(slots)?.map((slot) => (
            <button
              key={slot}
              type="button"
              onClick={() => {
                segmentTrackingEvents?.clickDate &&
                  track(segmentTrackingEvents?.clickDate)
                setSelectedDate(slot)
                setSelectedTime(null)
              }}
              data-testid={slot}
              className={`font-600 focus:outline-none border border-grey-500 rounded-lg py-2 px-2 lg:px-4 hover:border-blue-200 text-sm lg:text-base ${
                selectedDate === slot ? 'bg-blue-200 text-white' : 'text-grey'
              }`}
            >
              {i18n.date(date(slot, 'DD-MM-YYYY').toDate(), {
                day: '2-digit',
                month: 'short',
                weekday: 'short',
              })}
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center">
          <span className="text-grey font-600 text-xl">
            <Trans id="trialClass.booking.selectTime">Select a time</Trans>
          </span>
          <div className="ml-2 text-base text-grey-800 flex items-center font-medium">
            ({selectedTimezone}
            <button
              className="ml-1 text-blue-200 text-sm underline cursor-pointer hover:text-orange focus:outline-none"
              onClick={() => setIsTimezoneModalOpen(true)}
            >
              <Trans>Change</Trans>
            </button>
            )
          </div>
        </div>
        <div
          className="flex flex-wrap mt-2 overflow-auto lg:grid-cols-5 thin-scroll-bar"
          style={{
            maxHeight: `${
              sm
                ? MOBILE_SLOT_ROW_HEIGHT * MOBILE_SLOT_ROW_COUNT
                : DESKTOP_SLOT_ROW_HEIGHT * DESKTOP_SLOT_ROW_COUNT
            }px`,
          }}
        >
          {slotsToDisplay.map((slot) => (
            <div key={slot} className="flex">
              <button
                type="button"
                data-testid="time-slot-button"
                onClick={() => {
                  setSelectedTime(slot)
                  segmentTrackingEvents?.clickTime &&
                    track(segmentTrackingEvents.clickTime)
                }}
                className={`font-600 focus:outline-none border border-grey-500 w-32 rounded-lg mr-2 mb-2 py-2 px-2 lg:px-4 text-sm lg:text-base hover:border-blue-200 ${
                  selectedTime === slot ? 'bg-blue-200 text-white' : 'bg-white'
                }`}
              >
                {formatTimeByTimezone(
                  date(slot).toDate(),
                  selectedTimezone,
                  'hh:mm A'
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
      {error && <AlertMessage className="self-start">{error}</AlertMessage>}

      {showLimitedSlotsAlert &&
        selectedDate === date().tz(selectedTimezone).format('DD-MM-YYYY') &&
        slotsToDisplay.length <= LIMITED_SLOTS_THRESHOLD && (
          <AlertMessage type="info" className="self-start">
            <Trans id="trialClass.booking.onlyOneSlotLeftAlert">
              Only {slotsToDisplay.length}{' '}
              {slotsToDisplay.length > 1 ? 'slots' : 'slot'} left for today,
              hurry up!
            </Trans>
          </AlertMessage>
        )}
      <div className="flex items-center mt-3 space-x-5">
        <button
          className={`btn btn-orange py-3 flex justify-center rounded-lg ${
            isBooking ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
          data-testid="book-trial-class-btn"
          onClick={() => {
            setShowModal(true),
              track(SegmentEventEnum.TRIAL_CLASS_BOOKING_FLOW_CLICK_BOOK_NOW)
          }}
          disabled={isBooking}
        >
          {isBooking ? <Spinner classes="h-5 w-5 text-white" /> : ctaText}
        </button>
        {onGoBack && (
          <button
            className="hover:underline font-600 focus:outline-none"
            onClick={onGoBack}
          >
            <Trans id="trialClass.booking.goBack">Cancel</Trans>
          </button>
        )}
      </div>
      {!isShowingMoreSlots && isShowMoreSlotsOption && (
        <button
          className="font-600 text-lg hover:underline text-orange active:outline-none focus:outline-none my-3 w-56 text-left hover:text-orange-700"
          onClick={() => {
            setNumDaysToShow(7)
            setIsShowingMoreSlots(true)
          }}
        >
          I need more slot options
        </button>
      )}
      <AlertMessage
        type="warning"
        className="self-start bg-yellow-100 mt-4 lgLmt-6"
      >
        <span className="text-grey">
          {isMathClass ? (
            <Trans id="trialClass.booking.warningMessage.math">
              Your assigned Math Instructor will be available during your chosen
              class time. Please ensure you can attend when you book, as missing
              the class would waste their valuable time.
            </Trans>
          ) : (
            <Trans id="trialClass.booking.warningMessage">
              Your assigned Coding Instructor will be available during your
              chosen class time. Please ensure you can attend when you book, as
              missing the class would waste their valuable time.
            </Trans>
          )}
        </span>
      </AlertMessage>
      {showBookingStats && (
        <div className="flex items-center mt-5 space-x-3 mb-3">
          <div className="p-3 rounded-full bg-red-50">
            <img
              src={staticFile('/images/trial-class/check-mark-calendar.svg')}
              className="w-10 lg:w-4"
            />
          </div>
          <p className="max-w-lg text-sm leading-tight">
            {isMathClass ? (
              <Trans id="trialClass.booking.hurryUpMsg.math">
                1000+ students have taken a free lesson in last 24 hours! Hurry
                up, only limited slots available.
              </Trans>
            ) : (
              <Trans id="trialClass.booking.hurryUpMsg">
                1000+ students have taken a free coding class in last 24 hours!
                Hurry up, only limited slots available.
              </Trans>
            )}
          </p>
        </div>
      )}

      <TimezoneModal
        isOpen={isTimezoneModalOpen}
        setIsOpen={setIsTimezoneModalOpen}
        selectedTimezone={selectedTimezone}
        setSelectedTimezone={setSelectedTimezone}
      />

      <Modal
        isOpen={showModal}
        setIsOpen={setShowModal}
        shouldCloseOnEsc={true}
      >
        <div className="text-lg font-500 space-y-2">
          <p className="text-xl md:text-xl mb-5 text-center">
            {isMathClass ? (
              <Trans id="trialClass.booking.confirmationMsg.math">
                Are you sure that you are available at the selected date & time
                to join the free math class?
              </Trans>
            ) : (
              <Trans id="trialClass.booking.confirmationMsg.coding">
                Are you sure that you are available at the selected date & time
                to join the free coding class?
              </Trans>
            )}
          </p>
          <AlertMessage type="warning" className="bg-yellow-100">
            <span className="text-grey">
              {isMathClass ? (
                <Trans id="trialClass.booking.modal.warningMessage.math">
                  Your Math Instructor will wait for you in the class. If you
                  don&apos;t join, their precious time will be wasted. Confirm
                  only if you are sure that you will join.
                </Trans>
              ) : (
                <Trans id="trialClass.booking.modal.warningMessage.coding">
                  Your Coding Instructor will wait for you in the class. If you
                  don&apos;t join, their precious time will be wasted. Confirm
                  only if you are sure that you will join.
                </Trans>
              )}
            </span>
          </AlertMessage>
          <div className="pt-4 pb-2 space-y-3 text-base md:text-xl">
            <Button className="w-full" onClick={handleBooking}>
              <Trans>Yes, I will attend</Trans>
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleChangeSlot}
            >
              <Trans>No, I want to change date/time</Trans>
            </Button>
          </div>

          {error && (
            <div className="text-sm leading-4 text-red-200 font-400">
              {error}
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default AvailableSlots
